import React, { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Magnetic from "@/components/landing/Magnetic";

const products = [
  {
    name: "The Katoomba Hood",
    sub: "Heavy Cotton · Midnight",
    price: "A$ 240",
    img: "https://static.prod-images.emergentagent.com/jobs/3d06aecc-7c40-4cf7-8c1b-6c21e0f317e1/images/7439f2581147dd804cedf3a5a1de9c8e3377a2d490f1690489b6bbe80b788aee.png",
  },
  {
    name: "The Leura Crew",
    sub: "Brushed Fleece · Smoke",
    price: "A$ 210",
    img: "https://static.prod-images.emergentagent.com/jobs/3d06aecc-7c40-4cf7-8c1b-6c21e0f317e1/images/643a43138585501f8d4cf099de856561ce7bb5dffb3d6c67ac65d8e15c7b5ca1.png",
  },
  {
    name: "The Govetts Sweater",
    sub: "Garment Dyed · Ink",
    price: "A$ 265",
    img: "/images/drops/govetts.png",
  },
  {
    name: "The Wentworth Hood",
    sub: "Loop-back · Bone",
    price: "A$ 255",
    img: "/images/drops/wentworth.png",
  },
  {
    name: "The Three Sisters",
    sub: "Limited Edition · Gold",
    price: "A$ 320",
    img: "/images/drops/three-sisters.png",
  },
];

export default function Gallery() {
  const scrollerRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  const updateState = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const pad = parseFloat(getComputedStyle(el).paddingLeft) || 0;
    const max = el.scrollWidth - el.clientWidth;
    const p = max > pad ? Math.min(1, Math.max(0, (el.scrollLeft - pad) / (max - pad))) : 0;
    setProgress(p);
    setCanPrev(el.scrollLeft > pad + 4);
    setCanNext(el.scrollLeft < max - 4);
  };

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateState();
    el.addEventListener("scroll", updateState, { passive: true });
    window.addEventListener("resize", updateState);
    return () => {
      el.removeEventListener("scroll", updateState);
      window.removeEventListener("resize", updateState);
    };
  }, []);

  // Clipped-frame wipe — own IntersectionObserver so it is reliable
  // inside the horizontal track (fires for vertical page scroll AND
  // horizontal swipes that bring a card into view).
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add("card-revealed");
            io.unobserve(en.target);
          }
        });
      },
      { threshold: 0.15 },
    );
    el.querySelectorAll("[data-card-reveal]").forEach((c) => io.observe(c));
    return () => io.disconnect();
  }, []);

  const scrollByCard = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector("[data-card]");
    const cardW = card ? card.getBoundingClientRect().width + 24 : 400;
    el.scrollBy({ left: dir * cardW, behavior: "smooth" });
  };

  // Allow vertical-wheel → horizontal-scroll inside the track
  const onWheel = (e) => {
    const el = scrollerRef.current;
    if (!el) return;
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      el.scrollBy({ left: e.deltaY, behavior: "auto" });
      e.preventDefault();
    }
  };

  // Cursor spotlight — track position per card via CSS variables
  const onSpotMove = (e) => {
    const el = e.currentTarget;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - r.left}px`);
    el.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="relative bg-[#0a0f1d] py-24 md:py-32"
    >
      {/* Header */}
      <div className="px-6 md:px-12 lg:px-16 max-w-[1400px] mx-auto w-full">
        <div className="grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 md:col-span-6">
            <span className="label-overline">02 · The Collection</span>
            <h3 className="mt-4 font-serif-display text-white text-4xl md:text-6xl font-light leading-[1]">
              Drop <em className="italic text-[#D4AF37]">N°01</em>
            </h3>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8 flex flex-col md:items-end gap-6">
            <p className="text-white/65 text-[14px] leading-relaxed md:text-right">
              Five silhouettes. Each cut once, sewn slowly, and shipped from
              Katoomba. Drag, swipe, or use the arrows to wander the collection.
            </p>
            <div className="flex gap-3">
              <Magnetic strength={0.4}>
                <button
                  onClick={() => scrollByCard(-1)}
                  disabled={!canPrev}
                  data-testid="gallery-prev"
                  aria-label="Previous"
                  className="group w-12 h-12 border border-[#D4AF37]/40 text-[#D4AF37] flex items-center justify-center transition-all duration-300 hover:bg-[#D4AF37] hover:text-[#0a0f1d] disabled:opacity-25 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#D4AF37]"
                >
                  <ArrowLeft size={18} />
                </button>
              </Magnetic>
              <Magnetic strength={0.4}>
                <button
                  onClick={() => scrollByCard(1)}
                  disabled={!canNext}
                  data-testid="gallery-next"
                  aria-label="Next"
                  className="group w-12 h-12 border border-[#D4AF37]/40 text-[#D4AF37] flex items-center justify-center transition-all duration-300 hover:bg-[#D4AF37] hover:text-[#0a0f1d] disabled:opacity-25 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#D4AF37]"
                >
                  <ArrowRight size={18} />
                </button>
              </Magnetic>
            </div>
          </div>
        </div>
      </div>

      {/* Horizontal scrollable track */}
      <div
        ref={scrollerRef}
        onWheel={onWheel}
        data-lenis-prevent
        data-testid="gallery-track"
        className="mt-12 md:mt-16 flex gap-6 md:gap-10 overflow-x-auto overflow-y-hidden snap-x snap-mandatory px-6 md:px-12 lg:px-16 pb-12 scroll-smooth"
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <style>{`
          [data-testid="gallery-track"]::-webkit-scrollbar { display: none; }
        `}</style>

        {products.map((p, i) => (
          <article
            key={i}
            data-card
            data-card-reveal
            data-testid={`product-card-${i}`}
            onMouseMove={onSpotMove}
            className="relative shrink-0 snap-start w-[78vw] sm:w-[58vw] md:w-[42vw] lg:w-[32vw] xl:w-[28vw] h-[60vh] md:h-[70vh] group cursor-grab active:cursor-grabbing"
          >
            {/* Clipped frame — content wipes up out of the mask on scroll-in */}
            <div className="absolute inset-0 overflow-hidden border border-white/5">
              <div
                className="gallery-card-inner absolute inset-0"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <img
                  src={p.img}
                  alt={p.name}
                  draggable={false}
                  className="w-full h-full object-cover saturate-[0.9] contrast-[1.03] group-hover:saturate-100 transition-[transform,filter] duration-[1400ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.06] select-none pointer-events-none"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1d] via-transparent to-transparent" />
                {/* Cursor spotlight — gold wash that follows the mouse */}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(360px circle at var(--mx, 50%) var(--my, 50%), rgba(212,175,55,0.16), transparent 62%)",
                  }}
                />
                <div className="absolute top-5 left-5 wordmark-plate px-3 py-1.5">
                  <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] uppercase">
                    N° 0{i + 1}
                  </span>
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 z-10">
                  <div className="flex items-end justify-between gap-4">
                    <div>
                      <div className="text-[11px] tracking-[0.3em] uppercase text-[#D4AF37] mb-2">
                        {p.sub}
                      </div>
                      <h4 className="font-serif-display text-2xl md:text-3xl font-light">
                        {p.name}
                      </h4>
                    </div>
                    <div className="font-serif-display italic text-white/85 text-xl md:text-2xl">
                      {p.price}
                    </div>
                  </div>
                </div>
              </div>
              {/* Clipped inner frame — sharpens on hover */}
              <div className="absolute inset-4 border border-[#D4AF37]/0 group-hover:border-[#D4AF37]/45 transition-colors duration-700 pointer-events-none z-20" />
            </div>
          </article>
        ))}

        {/* End card with live drop countdown */}
        <article
          data-card
          className="shrink-0 snap-start w-[78vw] sm:w-[58vw] md:w-[42vw] lg:w-[32vw] xl:w-[28vw] h-[60vh] md:h-[70vh] flex items-center justify-center border border-[#D4AF37]/30"
        >
          <div className="text-center px-8">
            <span className="label-overline inline-flex items-center gap-2">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-pulse" />
              Drop closes
            </span>
            <h4 className="mt-6 font-serif-display text-4xl md:text-5xl font-light">
              Equinox
              <br />
              <em className="italic text-[#D4AF37]">2026</em>
            </h4>
            <DropCountdown />
            <a
              href="#newsletter"
              data-testid="gallery-cta-end"
              className="mt-10 inline-block gold-link text-xs tracking-[0.3em] uppercase"
            >
              Notify me →
            </a>
          </div>
        </article>
      </div>

      {/* Progress bar */}
      <div className="px-6 md:px-12 lg:px-16 max-w-[1400px] mx-auto">
        <div className="flex items-center gap-4">
          <span className="text-[10px] tracking-[0.3em] text-white/40 uppercase whitespace-nowrap">
            Drag · Swipe · Scroll
          </span>
          <div className="relative flex-1 h-px bg-white/10">
            <motion.div
              animate={{ scaleX: progress }}
              transition={{ duration: 0.2, ease: "linear" }}
              className="absolute inset-0 origin-left bg-[#D4AF37]"
              style={{ transformOrigin: "left" }}
            />
          </div>
          <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] uppercase whitespace-nowrap tabular-nums">
            {Math.round(progress * 100)}%
          </span>
        </div>
      </div>
    </section>
  );
}

/* Live countdown to the Equinox drop close — 22 Sep 2026, 9am Katoomba time */
const DROP_DATE = new Date("2026-09-22T09:00:00+10:00").getTime();

function DropCountdown() {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const diff = Math.max(0, DROP_DATE - now);
  const units = [
    ["days", Math.floor(diff / 86400000)],
    ["hrs", Math.floor(diff / 3600000) % 24],
    ["min", Math.floor(diff / 60000) % 60],
    ["sec", Math.floor(diff / 1000) % 60],
  ];

  return (
    <div
      data-testid="drop-countdown"
      className="mt-9 flex items-start justify-center gap-4 md:gap-5"
    >
      {units.map(([label, value], i) => (
        <div key={label} className="flex items-start gap-4 md:gap-5">
          {i > 0 && (
            <span
              aria-hidden
              className="font-serif-display text-[#D4AF37]/40 text-2xl md:text-3xl leading-[1.1] select-none"
            >
              ·
            </span>
          )}
          <div className="text-center">
            <div
              data-testid={`countdown-${label}`}
              className="font-serif-display text-3xl md:text-4xl text-[#D4AF37] font-light tabular-nums leading-none"
            >
              {String(value).padStart(2, "0")}
            </div>
            <div className="mt-2 text-[9px] tracking-[0.3em] uppercase text-white/45">
              {label}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
